import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, AlertCircle, CheckCircle2 } from "lucide-react";
import GoogleSignInButton from "@/components/shared/GoogleSignInButton";
import BackToHomepageLink from "@/components/shared/BackToHomepageLink";
import EarlyAccessBanner from "@/components/shared/EarlyAccessBanner";

export default function RegisterPage() {
  const { register, token } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company_name: "", country: "GB" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  if (token && !success) { navigate("/dashboard", { replace: true }); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.full_name || !form.company_name) {
      setError("All fields are required"); return;
    }
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setError("");
    setLoading(true);
    try {
      const result = await register(form);
      setSuccess({ company: form.company_name, name: form.full_name });
      // Auto-redirect after 3 seconds
      setTimeout(() => navigate("/dashboard", { replace: true }), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B1120] p-6" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Success Confirmation */}
        {success ? (
          <div className="text-center" data-testid="register-success">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="font-barlow font-bold text-2xl text-slate-50 mb-2">Welcome to Sentient Vault</h1>
            <p className="text-lg text-emerald-400 font-semibold mb-1">{success.company}</p>
            <p className="text-sm text-slate-400 mb-6">Your company has been successfully created with a 30-day free trial. Full access is now active.</p>
            <div className="p-4 rounded-xl border border-slate-800/40 bg-slate-900/30 mb-6 text-left space-y-2">
              <div className="flex justify-between text-xs"><span className="text-slate-500">Company</span><span className="text-slate-200">{success.company}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Account Owner</span><span className="text-slate-200">{success.name}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Plan</span><span className="text-cyan-400 font-mono">30-Day Free Trial</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">Access Level</span><span className="text-emerald-400 font-mono">Full (Pro)</span></div>
            </div>
            <Button onClick={() => navigate("/dashboard", { replace: true })} className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-xl" data-testid="register-go-dashboard">
              Go to Dashboard
            </Button>
            <p className="text-[10px] text-slate-600 mt-3">Redirecting automatically in a few seconds...</p>
          </div>
        ) : (
        <>
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="register-logo-link"
          >
            <img src="https://customer-assets.emergentagent.com/job_vault-truth/artifacts/s3q6fukn_image.png" alt="Sentient Vault" className="w-9 h-9" />
            <span className="font-barlow font-bold text-lg text-slate-50">Sentient Vault</span>
          </Link>
          <BackToHomepageLink
            testId="register-back-to-homepage"
            label="Home"
          />
        </div>
        <div className="text-center mb-6">
          <h1 className="font-barlow font-bold text-2xl text-slate-50">Create Your Account</h1>
          <p className="text-sm text-slate-500 mt-1">Start your 30-day free trial. No credit card required.</p>
        </div>
        <div className="mb-6">
          <EarlyAccessBanner
            variant="compact"
            testId="register-early-access-banner"
          />
        </div>

        <div className="p-6 rounded-2xl border border-slate-800/50 bg-slate-900/50">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Full Name</Label>
              <Input value={form.full_name} onChange={e => set("full_name", e.target.value)} placeholder="David Al Rashid"
                className="bg-slate-800/50 border-slate-700 text-slate-100 h-10" data-testid="register-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Company Name</Label>
              <Input value={form.company_name} onChange={e => set("company_name", e.target.value)} placeholder="Al Rashid Trading LLC"
                className="bg-slate-800/50 border-slate-700 text-slate-100 h-10" data-testid="register-company" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Country</Label>
              <Select value={form.country} onValueChange={v => set("country", v)}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 h-10" data-testid="register-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="GB">United Kingdom</SelectItem>
                  <SelectItem value="AE">United Arab Emirates</SelectItem>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="DE">Germany</SelectItem>
                  <SelectItem value="ES">Spain</SelectItem>
                  <SelectItem value="MA">Morocco</SelectItem>
                  <SelectItem value="HK">Hong Kong</SelectItem>
                  <SelectItem value="AU">Australia</SelectItem>
                  <SelectItem value="CN">China</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Email</Label>
              <Input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@company.com"
                className="bg-slate-800/50 border-slate-700 text-slate-100 h-10" data-testid="register-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Password</Label>
              <Input type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder="Minimum 8 characters"
                className="bg-slate-800/50 border-slate-700 text-slate-100 h-10" data-testid="register-password" />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="register-error">
                <AlertCircle className="w-4 h-4 shrink-0" />{error}
              </div>
            )}
            <Button type="submit" disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold rounded-xl"
              data-testid="register-submit-btn">
              {loading ? "Creating account..." : "Start 30-Day Free Trial"}
            </Button>
          </form>

          {/* Google signup — same provider as login, just a
              different testid + label. */}
          <div className="relative my-5" data-testid="register-or-divider">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-800" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-[0.2em] font-manrope">
              <span className="bg-[#0B1120] px-3 text-slate-600">or</span>
            </div>
          </div>
          <GoogleSignInButton
            label="Sign up with Google"
            testId="google-signin-btn-register"
          />

          <div className="mt-4 flex items-center gap-2 justify-center">
            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
            <span className="text-[10px] text-slate-600">Read-only bank mirroring. We never hold your funds.</span>
          </div>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account? <Link to="/login" className="text-amber-400 hover:text-amber-300">Sign in</Link>
        </p>
        </>
        )}
      </div>
    </div>
  );
}
